/**
 * Ação bloqueada que explica o motivo.
 *
 * O próprio botão é o gatilho: fica `aria-disabled` (não `disabled`), então
 * continua focável pelo teclado e recebe hover e toque — um botão `disabled`
 * não recebe nenhum desses eventos, e tooltip de hover não existe no celular.
 * Bloqueado, o clique só mostra o motivo; a ação original nunca roda.
 *
 * O popover é ancorado, não acionado por `PopoverTrigger`: o trigger alterna no
 * clique, e um toque no celular dispara hover + foco + clique — o aviso abriria
 * e fecharia no mesmo toque. Aqui esses eventos só ABREM. O hover vem dos
 * eventos de ponteiro e vale só para mouse: no toque, o navegador emula
 * entrada/saída do "mouse" em volta do clique, e essa saída emulada fecharia o
 * aviso logo depois de abrir. No toque, fecha ao tocar fora ou no Esc.
 */
import {
  Children,
  cloneElement,
  isValidElement,
  useId,
  useState,
  type MouseEvent,
  type PointerEvent,
  type ReactElement,
} from 'react';

import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

interface ActionProps {
  className?: string;
  onClick?: (event: MouseEvent<HTMLElement>) => void;
}

interface LockedActionHintProps {
  /** Motivo do bloqueio; `null` deixa a ação livre, sem aviso. */
  message: string | null;
  /** Um único elemento de ação (botão). */
  children: ReactElement<ActionProps>;
}

export function LockedActionHint({ message, children }: Readonly<LockedActionHintProps>) {
  const [open, setOpen] = useState(false);
  const descriptionId = useId();

  const action = Children.only(children);
  if (!message || !isValidElement<ActionProps>(action)) return action;

  const abrir = () => setOpen(true);
  const fechar = () => setOpen(false);
  const soMouse = (acao: () => void) => (event: PointerEvent<HTMLElement>) => {
    if (event.pointerType === 'mouse') acao();
  };

  const bloqueada = cloneElement(action, {
    'aria-disabled': true,
    'aria-describedby': descriptionId,
    // Visual cinza próprio, sem efeito de hover, em qualquer variante (contorno,
    // verde ou o laranja do "Popular"): o tailwind-merge faz estas classes
    // vencerem as da variante, que pintariam o botão bloqueado no hover.
    className: cn(
      action.props.className,
      'cursor-not-allowed border-border bg-muted text-muted-foreground shadow-none hover:bg-muted hover:text-muted-foreground',
    ),
    onClick: (event: MouseEvent<HTMLElement>) => {
      event.preventDefault();
      abrir();
    },
    onPointerEnter: soMouse(abrir),
    onPointerLeave: soMouse(fechar),
    onFocus: abrir,
    onBlur: fechar,
  } as Partial<ActionProps>);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverAnchor asChild>{bloqueada}</PopoverAnchor>
      <span id={descriptionId} className="sr-only">
        {message}
      </span>
      <PopoverContent
        role="tooltip"
        side="top"
        className="w-72 p-3 text-xs leading-snug"
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        {message}
      </PopoverContent>
    </Popover>
  );
}
